/**
 * The configuration editor's information architecture: which categories each platform pane
 * has, in what order, under which group heading, and which configuration keys each one owns.
 *
 * This is the ONE place that knows it. The pane renders a section per entry, the rail draws
 * an item per entry, the inheritance status block reads `inheritKeys`, and the E2E page
 * object derives its category map from here rather than keeping a second copy of the IA.
 * That last consumer is why this file is plain data: no React import, no side effects, so it
 * can be imported from outside `designer/src`.
 *
 * A category id is a stable identifier, not a label. It reaches the DOM as
 * `data-config-category` and as the rail item's `data-testid`, so renaming one is a
 * test-visible change.
 */

/** The `IConfig` slice a pane edits — also the first segment of every form path it writes. */
export type ConfigPaneKey = "apiConfig" | "webConfig" | "iosConfig" | "androidConfig" | "electronConfig";

/** Which `public/images` folder a category's glyph comes from. */
export type ConfigCategoryIconType = "common" | "datasource-icons";

export interface IConfigCategory {
  /** Stable id. Reaches the DOM; never rename without updating the E2E page object. */
  id: string;
  /** The rail heading this category sits under. Repeated across consecutive entries. */
  group: string;
  /**
   * Title Case, in the rail AND as the section heading — the heading uppercases in CSS so
   * the two never drift apart. Acronyms (AI, MCP) keep their casing.
   */
  label: string;
  /**
   * How many rows the category shows. The rail prints it only from 4 up, where a count
   * stops being noise and starts telling you the section is long. Hand-maintained: it is a
   * scanning hint, not a computed truth.
   */
  fieldCount: number;
  /**
   * The keys on the pane's configuration slice whose value this category owns. Feeds the
   * override dot, the Inherited/Set here/Overridden block, and Restore.
   *
   * Listed even where the pane does not forward `parentConfigValue` to the control today:
   * `setParentChildDifferenceOfConfigs` diffs EVERY key of the slice, so the signal exists
   * whether or not a row draws it.
   */
  /** The rail's glyph. */
  icon: string;
  /**
   * The folder it comes from, when it is not the default `common` set. Spelled out here
   * rather than imported from `KuikaSvg`: this file is plain data with no React in it, so the
   * E2E suite can derive its category map from the same source the rail draws from.
   */
  iconType?: ConfigCategoryIconType;
  /**
   * The keys of `parentChildDifference` that decide whether this category is inherited.
   * Empty means the question cannot be answered — the setting is not on a compared slice —
   * and the status block and rail dot are omitted rather than guessed at.
   */
  inheritKeys: string[];
  /**
   * The slice those keys live on, when it is not the pane's own. Two categories are filed
   * under one platform but write another's slice.
   */
  inheritSlice?: ConfigPaneKey;
}

/**
 * The App Settings pane. 24 categories — the reason this pane needed a rail at all.
 */
export const APP_SETTINGS_CATEGORIES: IConfigCategory[] = [
  {
    id: "kuikaCloud",
    group: "Connectivity",
    label: "Kuika Cloud",
    icon: "cloud-cog",
    iconType: "datasource-icons",
    fieldCount: 0,
    // `isKuikaCloud` is a root-level key of `IConfig`, and the container's difference walks
    // the platform slices only — so this category has nothing to read.
    inheritKeys: []
  },
  {
    id: "connectionSettings",
    group: "Connectivity",
    label: "Connection Settings",
    icon: "link",
    fieldCount: 4,
    inheritKeys: [
      "dbConnectionStringSettingsId",
      "backendUrlSettingsId",
      "fileUploadPathSettingsId",
      "sapServiceBaseUrlSettingsId"
    ]
  },
  {
    id: "datasourceConfigurations",
    group: "Connectivity",
    label: "Datasource Configurations",
    icon: "database-cog",
    // One row per REST/Swagger/OData datasource in the project, so the count is a floor
    // rather than a fact — under the badge threshold it is never printed anyway.
    fieldCount: 0,
    inheritKeys: ["restApiDatasourceBindings"]
  },
  {
    id: "rateLimiting",
    group: "Connectivity",
    label: "Rate Limiting",
    icon: "adjustments-horizontal",
    fieldCount: 1,
    inheritKeys: ["ipRateLimitingSettingsId"]
  },

  {
    id: "documentManagement",
    group: "Storage & Search",
    label: "Document Management",
    icon: "file-description",
    fieldCount: 1,
    inheritKeys: ["documentManagementSettingsId"]
  },
  {
    id: "fullTextSearchEngine",
    group: "Storage & Search",
    label: "Full-Text Search Engine",
    icon: "list-search",
    fieldCount: 1,
    inheritKeys: ["searchEngineSettingsId"]
  },
  {
    id: "bucketUpload",
    group: "Storage & Search",
    label: "Bucket Upload",
    icon: "archive",
    fieldCount: 1,
    inheritKeys: ["bucketUploadSettingsId"]
  },
  {
    id: "cacheSettings",
    group: "Storage & Search",
    label: "Cache Settings",
    icon: "memory",
    iconType: "datasource-icons",
    fieldCount: 1,
    inheritKeys: ["cacheSettingsId"]
  },
  {
    id: "searchService",
    group: "Storage & Search",
    label: "Search Service",
    icon: "search",
    fieldCount: 1,
    inheritKeys: ["searchServiceSettingsId"]
  },

  {
    id: "authentication",
    group: "Identity & Access",
    label: "Authentication & Authorization",
    icon: "lock",
    fieldCount: 8,
    inheritKeys: ["authProviderSettingsId", "passwordPoliciesId", "googleAuthId", "msalAuthId", "otpSettingsId"]
  },
  {
    id: "authenticationCookies",
    group: "Identity & Access",
    label: "Authentication Cookies",
    icon: "browser",
    fieldCount: 2,
    inheritKeys: ["cookieAllowCrossSite", "cookieAllowHttp"]
  },

  { id: "mail", group: "Integrations", label: "Mail", icon: "mail", fieldCount: 1, inheritKeys: ["mailSettingsId"] },
  {
    id: "ai",
    group: "Integrations",
    label: "AI",
    icon: "ai-sparkle",
    fieldCount: 4,
    inheritKeys: ["openAISettingsId", "azureOpenAISettingsId", "geminiSettingsId", "serperToolSettingsId"]
  },
  {
    id: "paymentMethod",
    group: "Integrations",
    label: "Payment Method",
    icon: "credit-card",
    fieldCount: 3,
    inheritKeys: ["stripeSettingsId", "iyzicoSettingsId", "paramPaymentSettingsId"]
  },
  {
    id: "whatsappBusiness",
    group: "Integrations",
    label: "WhatsApp Business",
    icon: "message",
    fieldCount: 1,
    inheritKeys: ["whatsappBusinessSettingsId"]
  },
  {
    id: "inversCloudBoxx",
    group: "Integrations",
    label: "Invers CloudBoxx",
    icon: "briefcase",
    fieldCount: 1,
    inheritKeys: ["inversCloudBoxxSettingsId"]
  },
  {
    id: "googleRecaptcha",
    group: "Integrations",
    label: "Google reCAPTCHA",
    icon: "shield-check",
    fieldCount: 1,
    inheritKeys: ["googleRecaptchaSettingsId"]
  },
  {
    id: "mcp",
    group: "Integrations",
    label: "MCP",
    icon: "puzzle",
    fieldCount: 13,
    inheritKeys: [
      "braveSearchSettingsId",
      "slackSettingsId",
      "dropboxSettingsId",
      "githubSettingsId",
      "googleMcpSettingsId",
      "amapWebServiceSettingsId",
      "googleMapMcpSettingsId",
      "jiraMcpSettingsId",
      "youtubeMcpSettingsId",
      "trelloMcpSettingsId",
      "office365McpSettingsId",
      "firecrawlMcpSettingsId",
      "apifySettingsId"
    ]
  },

  {
    id: "swaggerUi",
    group: "Operations",
    label: "Swagger UI",
    icon: "swagger",
    iconType: "datasource-icons",
    fieldCount: 4,
    inheritKeys: ["useSwaggerUi", "swaggerUiUsername", "swaggerUiPassword"]
  },
  {
    id: "elasticApm",
    group: "Operations",
    label: "Elastic APM",
    icon: "scan-eye",
    fieldCount: 1,
    inheritKeys: ["elasticApmSettingsId"]
  },
  {
    id: "notifyActions",
    group: "Operations",
    label: "Notify Actions",
    icon: "alert-circle",
    fieldCount: 1,
    inheritKeys: ["notifyActionsDefaultDurationSettingsId"]
  },
  {
    id: "errorMessages",
    group: "Operations",
    label: "Error Messages",
    icon: "eye-off",
    fieldCount: 1,
    inheritKeys: ["errorMessageMode"]
  },
  {
    id: "deeplinkSetting",
    group: "Operations",
    label: "Deeplink Setting",
    icon: "external-link",
    fieldCount: 1,
    inheritKeys: ["commonMobileSettingsId"]
  },
  {
    id: "dateTimeFormats",
    group: "Operations",
    label: "Date & Time Formats",
    icon: "calendar-time",
    fieldCount: 2,
    // The section is filed under App Settings but the fields are `webConfig`'s — see
    // `ConfigDateFormat parentData="webConfig"` in the pane.
    inheritKeys: ["dateFormat", "timeFormat"],
    inheritSlice: "webConfig"
  }
];

export const WEB_CATEGORIES: IConfigCategory[] = [
  {
    id: "hostSettings",
    group: "Web App",
    label: "Host Settings",
    icon: "server",
    fieldCount: 1,
    inheritKeys: ["frontendUrlSettingsId"]
  },
  {
    id: "sessions",
    group: "Web App",
    label: "Sessions",
    icon: "clock",
    fieldCount: 1,
    inheritKeys: ["sessionSettingsId"]
  },
  {
    id: "webMaps",
    group: "Web App",
    label: "Maps",
    icon: "map",
    fieldCount: 3,
    inheritKeys: ["mapProvider", "googleMapsSettingsId", "amapSettingsId", "allowEndUserMapProviderChoice"]
  },
  {
    id: "webPushNotification",
    group: "Web App",
    label: "Push Notification",
    icon: "bell",
    fieldCount: 1,
    inheritKeys: ["pushNotificationSettingsId"]
  },
  {
    id: "kuikagentsIntegration",
    group: "Web App",
    label: "KuikAgents Integration",
    icon: "ai-bot",
    fieldCount: 1,
    // `ConfigTwinInput.formInputName` is `["apiConfig", "twinSettingsId"]`.
    inheritKeys: ["twinSettingsId"],
    inheritSlice: "apiConfig"
  }
];

export const IOS_CATEGORIES: IConfigCategory[] = [
  {
    id: "iosMobileAppSettings",
    group: "App",
    label: "Mobile App Settings",
    icon: "device-mobile",
    fieldCount: 4,
    inheritKeys: []
  },
  {
    id: "iosAppleSignIn",
    group: "App",
    label: "Apple Sign In Settings",
    icon: "user-circle",
    fieldCount: 1,
    inheritKeys: ["appleSigninSettingsId"]
  },
  {
    id: "iosAppStore",
    group: "App",
    label: "App Store Settings",
    icon: "app",
    fieldCount: 1,
    inheritKeys: ["appStoreConnectApiSettingsId"]
  },
  {
    id: "iosFirebase",
    group: "App",
    label: "Firebase Settings",
    icon: "lightning",
    fieldCount: 1,
    inheritKeys: ["firebaseSettingsId"]
  },

  {
    id: "iosGeolocation",
    group: "Device",
    label: "Geolocation",
    icon: "map-pin-pin",
    fieldCount: 1,
    inheritKeys: ["locationPermissionLabelSettingsId"]
  },
  {
    id: "iosCamera",
    group: "Device",
    label: "Camera",
    icon: "eye",
    fieldCount: 1,
    inheritKeys: ["cameraPermissionLabelSettingsId"]
  },
  {
    id: "iosBluetooth",
    group: "Device",
    label: "Bluetooth",
    icon: "bluetooth",
    iconType: "datasource-icons",
    fieldCount: 1,
    inheritKeys: ["bluetoothPermissionLabelSettingsId"]
  },
  {
    id: "iosGpsTracker",
    group: "Device",
    label: "GPS Tracker",
    icon: "route-square-2",
    fieldCount: 3,
    inheritKeys: [
      "gpsTrackingSettingsId",
      "gpsTrackingPermissionLabelSettingsId",
      "gpsTrackingBackgroundPermissionLabelSettingsId"
    ]
  },

  {
    id: "iosPushNotification",
    group: "Services",
    label: "Push Notification",
    icon: "bell",
    fieldCount: 2,
    inheritKeys: ["pushNotificationSettingsId", "notificationPermissionLabelSettingsId"]
  },
  {
    id: "iosGoogleRecaptcha",
    group: "Services",
    label: "Google reCAPTCHA",
    icon: "shield-check",
    fieldCount: 1,
    inheritKeys: ["googleRecaptchaIosSettingsId"]
  }
];

export const ANDROID_CATEGORIES: IConfigCategory[] = [
  {
    id: "androidMobileAppSettings",
    group: "App",
    label: "Mobile App Settings",
    icon: "device-mobile",
    fieldCount: 9,
    inheritKeys: []
  },
  {
    id: "androidPlayStore",
    group: "App",
    label: "Play Store Settings",
    icon: "google",
    fieldCount: 2,
    inheritKeys: ["googlePlayApiSettingsId", "adiRegistrationSettingsId"]
  },

  {
    id: "androidGeolocation",
    group: "Device",
    label: "Geolocation",
    icon: "map-pin-pin",
    fieldCount: 1,
    inheritKeys: ["locationPermissionLabelSettingsId"]
  },
  {
    id: "androidCamera",
    group: "Device",
    label: "Camera",
    icon: "eye",
    fieldCount: 1,
    inheritKeys: ["cameraPermissionLabelSettingsId"]
  },
  {
    id: "androidBluetooth",
    group: "Device",
    label: "Bluetooth",
    icon: "bluetooth",
    iconType: "datasource-icons",
    fieldCount: 1,
    inheritKeys: ["bluetoothPermissionLabelSettingsId"]
  },
  {
    id: "androidGpsTracker",
    group: "Device",
    label: "GPS Tracker",
    icon: "route-square-2",
    fieldCount: 3,
    inheritKeys: [
      "gpsTrackingSettingsId",
      "gpsTrackingPermissionLabelSettingsId",
      "gpsTrackingBackgroundPermissionLabelSettingsId"
    ]
  },

  {
    id: "androidMaps",
    group: "Services",
    label: "Maps",
    icon: "map",
    fieldCount: 3,
    inheritKeys: ["mapProvider", "googleMapsSettingsId", "amapSettingsId", "allowEndUserMapProviderChoice"]
  },
  {
    id: "androidPushNotification",
    group: "Services",
    label: "Push Notification",
    icon: "bell",
    fieldCount: 2,
    inheritKeys: ["firebaseSettingsId", "notificationPermissionLabelSettingsId"]
  },
  {
    id: "androidGoogleRecaptcha",
    group: "Services",
    label: "Google reCAPTCHA",
    icon: "shield-check",
    fieldCount: 1,
    inheritKeys: ["googleRecaptchaAndroidSettingsId"]
  }
];

/**
 * The Desktop App pane. It has no parent-configuration wiring at all — the container never
 * hands it `parentChildDifferences` — so every `inheritKeys` here is empty by design, and
 * the status block does not render for it.
 */
export const ELECTRON_CATEGORIES: IConfigCategory[] = [
  {
    id: "electronAppSettings",
    group: "Desktop App",
    label: "Desktop App Settings",
    icon: "device-desktop",
    fieldCount: 3,
    inheritKeys: []
  },
  {
    id: "electronTargetPlatforms",
    group: "Desktop App",
    label: "Target Platforms",
    icon: "devices",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "electronArchitectures",
    group: "Desktop App",
    label: "Architectures",
    icon: "components",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "electronLinuxPackageFormats",
    group: "Desktop App",
    label: "Linux Package Formats",
    icon: "package",
    fieldCount: 1,
    inheritKeys: []
  }
];

/**
 * The categories a pane is actually drawing right now.
 *
 * A pane hides whole categories for reasons only it knows — the workspace surface has no
 * Kuika Cloud or Cache Settings, Deeplink needs a mobile project, Linux Package Formats
 * needs Linux to be a target. The pane computes those booleans once and uses the SAME ones
 * to gate its sections, so the rail can never offer a category the body does not have.
 */
/**
 * Run Settings.
 *
 * Not a platform pane and not a configuration: it is the settings the RUN/preview build
 * uses, and it draws from several slices at once — a web session next to an Android push
 * certificate. It was one 5300px scroll of twenty sections in a container that could not
 * scroll, so most of it was unreachable.
 *
 * Ids, labels and glyphs are the ones the platform panes already use for the same setting:
 * Mail is `mail` here as it is there. `webPushNotification` / `androidPushNotification` /
 * `iosPushNotification` are three different settings that share a label, so the group is
 * what tells them apart — which is why the mobile pair sits under its own heading rather
 * than beside the web one. `inheritKeys` is empty throughout: Run Settings has no parent
 * configuration to differ from, so the rail draws no override dots here.
 */
export const RUN_SETTINGS_CATEGORIES: IConfigCategory[] = [
  { id: "sessions", group: "Web App", label: "Sessions", icon: "clock", fieldCount: 1, inheritKeys: [] },
  { id: "webMaps", group: "Web App", label: "Maps", icon: "map", fieldCount: 2, inheritKeys: [] },
  {
    id: "webPushNotification",
    group: "Web App",
    label: "Push Notification",
    icon: "bell",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "authentication",
    group: "Identity & Access",
    label: "Authentication & Authorization",
    icon: "lock",
    fieldCount: 8,
    inheritKeys: []
  },
  {
    id: "documentManagement",
    group: "Storage & Search",
    label: "Document Management",
    icon: "file-description",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "fullTextSearchEngine",
    group: "Storage & Search",
    label: "Full-Text Search Engine",
    icon: "list-search",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "bucketUpload",
    group: "Storage & Search",
    label: "Bucket Upload",
    icon: "archive",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "cacheSettings",
    group: "Storage & Search",
    label: "Cache Settings",
    icon: "memory",
    iconType: "datasource-icons",
    fieldCount: 1,
    inheritKeys: []
  },
  { id: "mail", group: "Integrations", label: "Mail", icon: "mail", fieldCount: 1, inheritKeys: [] },
  { id: "ai", group: "Integrations", label: "AI", icon: "ai-sparkle", fieldCount: 4, inheritKeys: [] },
  {
    id: "paymentMethod",
    group: "Integrations",
    label: "Payment Method",
    icon: "credit-card",
    fieldCount: 3,
    inheritKeys: []
  },
  {
    id: "inversCloudBoxx",
    group: "Integrations",
    label: "Invers CloudBoxx",
    icon: "briefcase",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "whatsappBusiness",
    group: "Integrations",
    label: "WhatsApp Business",
    icon: "message",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "googleRecaptcha",
    group: "Integrations",
    label: "Google reCAPTCHA",
    icon: "shield-check",
    fieldCount: 1,
    inheritKeys: []
  },
  { id: "mcp", group: "Integrations", label: "MCP", icon: "puzzle", fieldCount: 13, inheritKeys: [] },
  {
    id: "kuikagentsIntegration",
    group: "Integrations",
    label: "KuikAgents Integration",
    icon: "ai-bot",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "androidPushNotification",
    group: "Mobile",
    label: "Android Push Notification",
    icon: "bell",
    fieldCount: 2,
    inheritKeys: []
  },
  {
    id: "iosPushNotification",
    group: "Mobile",
    label: "iOS Push Notification",
    icon: "bell",
    fieldCount: 2,
    inheritKeys: []
  },
  {
    id: "notifyActions",
    group: "Operations",
    label: "Notify Actions",
    icon: "alert-circle",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "errorMessages",
    group: "Operations",
    label: "Error Messages",
    icon: "eye-off",
    fieldCount: 1,
    inheritKeys: []
  },
  {
    id: "dateTimeFormats",
    group: "Operations",
    label: "Date & Time Formats",
    icon: "calendar-time",
    fieldCount: 2,
    inheritKeys: []
  },
  { id: "buildType", group: "Operations", label: "Build Type", icon: "package", fieldCount: 1, inheritKeys: [] }
];

export const visibleCategories = (all: IConfigCategory[], hiddenIds: string[]): IConfigCategory[] => {
  if (hiddenIds.length === 0) return all;
  return all.filter((category: IConfigCategory): boolean => !hiddenIds.includes(category.id));
};

/** The rail prints a count only once a category is long enough for it to mean something. */
/**
 * Every pane's categories, by the pane that draws them.
 *
 * A category id is unique within a pane, not across the file — Sessions is a Web category
 * and a Run Settings one, deliberately sharing a label and a glyph — so anything looking a
 * category up has to say which pane it means. The search results do, to draw each row with
 * the glyph the rail uses for the place it leads to.
 */
export const CATEGORIES_BY_PANE: Record<ConfigPaneKey, IConfigCategory[]> = {
  apiConfig: APP_SETTINGS_CATEGORIES,
  webConfig: WEB_CATEGORIES,
  iosConfig: IOS_CATEGORIES,
  androidConfig: ANDROID_CATEGORIES,
  electronConfig: ELECTRON_CATEGORIES
};

/** One category of one pane, or undefined for a section that is not a rail category. */
export const findConfigCategory = (
  pane: ConfigPaneKey,
  categoryId: string | undefined
): IConfigCategory | undefined => {
  if (categoryId === undefined) return undefined;
  return CATEGORIES_BY_PANE[pane].find((category: IConfigCategory): boolean => category.id === categoryId);
};

export const CATEGORY_COUNT_BADGE_THRESHOLD: number = 4;

export interface IConfigCategoryGroup {
  name: string;
  categories: IConfigCategory[];
}

/**
 * The rail's shape: consecutive entries sharing a `group` become one block. Order comes from
 * the array, never from sorting — the IA is deliberate, and a group interrupted and resumed
 * would (correctly) draw its heading twice.
 */
export const groupCategories = (categories: IConfigCategory[]): IConfigCategoryGroup[] => {
  const groups: IConfigCategoryGroup[] = [];
  categories.forEach((category: IConfigCategory): void => {
    const last: IConfigCategoryGroup | undefined = groups[groups.length - 1];
    if (last && last.name === category.group) {
      last.categories.push(category);
      return;
    }
    groups.push({ name: category.group, categories: [category] });
  });
  return groups;
};
